import { useState, useCallback, useEffect, useRef } from 'react';
import { useDataCache } from '../context/DataCacheContext';
import { useErrorHandler } from './useErrorHandler';
import { isValidCachedCard } from '../utils/cardValidation';
import { createLogger } from '../utils/logger';
import { networkUtils, isNetworkError } from '../utils/networkUtils';

const logger = createLogger({ module: 'useCachedData' });

interface UseCachedDataOptions<T> {
  cacheKey: string;
  maxAge?: number;
  initialData?: T | null;
  onSuccess?: (data: T) => void;
  onError?: (error: Error) => void;
  skipCache?: boolean;
  validateFn?: (data: unknown) => data is T;
  useHybridCache?: boolean; // Enable hybrid cache for this hook
  enabled?: boolean; // Whether to enable data fetching
}

export function useCachedData<T>(
  fetchFn: () => Promise<T>,
  {
    cacheKey,
    maxAge = 5 * 60 * 1000, // 5 minutes
    initialData = null,
    onSuccess,
    onError,
    skipCache = false,
    validateFn,
    useHybridCache = false,
    enabled = true
  }: UseCachedDataOptions<T>
) {
  const [data, setData] = useState<T | null>(initialData);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<Error | null>(null);
  const { 
    getData, 
    setData: setCacheData, 
    getDataAsync,
    getInFlightRequest,
    setInFlightRequest,
    removeInFlightRequest
  } = useDataCache();
  const handleError = useErrorHandler('useCachedData');
  const mountedRef = useRef(true);
  const lastFetchAttemptRef = useRef<Record<string, number>>({});
  const NOT_FOUND_RETRY_DELAY = 5 * 60 * 1000; // 5 minutes
  const fetchingRef = useRef(false); // Track if we're currently fetching
  const retryCountRef = useRef<Record<string, number>>({});
  const backoffTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null);

  // Calculate exponential backoff delay
  const getBackoffDelay = useCallback((retryCount: number): number => {
    // Exponential backoff: 1s, 2s, 4s, 8s, 16s, 32s, then cap at 60s
    const baseDelay = 1000; // 1 second
    const maxDelay = 60000; // 60 seconds
    const exponentialDelay = baseDelay * Math.pow(2, retryCount);
    const jitteredDelay = exponentialDelay * (0.5 + Math.random() * 0.5); // Add jitter
    return Math.min(jitteredDelay, maxDelay);
  }, []);

  const validateData = useCallback((data: unknown): T | null => {
    logger.debug('Validating data:', { cacheKey, dataType: typeof data, isArray: Array.isArray(data) });
    
    // If it's an array of career cards, validate each one
    if (Array.isArray(data) && cacheKey.includes('CAREER_CARDS')) {
      logger.debug('Validating career cards array:', { length: data.length, firstCard: data[0] });
      const validCards = data.filter(isValidCachedCard);
      logger.debug('Validation result:', { 
        validCount: validCards.length, 
        totalCount: data.length,
        isValid: validCards.length === data.length,
        firstValidCard: validCards[0]
      });
      return validCards.length === data.length ? validCards as unknown as T : null;
    }
    
    // Validate notes array - ensure each note has at least id and title
    if (Array.isArray(data) && cacheKey.includes('NOTES')) {
      logger.debug('Validating notes array:', { length: data.length, firstNote: data[0] });
      const validNotes = data.filter(note => {
        const hasId = Boolean(note && typeof note === 'object' && 'id' in note && note.id);
        const hasTitle = Boolean(note && typeof note === 'object' && 'title' in note);
        const hasContent = Boolean(note && typeof note === 'object' && 'content' in note);
        return hasId && (hasTitle || hasContent);
      });
      
      logger.debug('Notes validation result:', { 
        validCount: validNotes.length, 
        totalCount: data.length,
        isValid: validNotes.length === data.length,
        firstValidNote: validNotes[0]
      });
      
      // Return valid notes even if some are invalid - this helps ensure we don't lose data
      return validNotes.length > 0 ? validNotes as unknown as T : null;
    }
    
    // Use provided validation function if available
    if (validateFn) {
      const isValid = validateFn(data);
      logger.debug('Custom validation result:', { isValid, data });
      return isValid ? data : null;
    }

    // Default case: return as is
    logger.debug('No validation required, returning as is');
    return data as T;
  }, [cacheKey, validateFn]);

  const fetchData = useCallback(async (force = false) => {
    logger.debug('fetchData called:', { force, skipCache, cacheKey });
    
    // Prevent concurrent fetches
    if (fetchingRef.current) {
      logger.debug('Already fetching, skipping:', { cacheKey });
      return;
    }
    
    // If we're offline and we have cached data, use it immediately
    const isCurrentlyOffline = networkUtils.isOffline();
    if (isCurrentlyOffline && !force) {
      logger.debug('Currently offline, checking for cached data:', { cacheKey });
      const cacheResult = getData<T>(cacheKey, Infinity); // Use infinite maxAge when offline
      
      if (cacheResult.data) {
        const validatedData = validateData(cacheResult.data);
        if (validatedData) {
          logger.debug('Using cached data while offline:', { cacheKey });
          setData(validatedData);
          onSuccess?.(validatedData);
          return;
        }
      }
      
      // No cached data available while offline
      logger.debug('No cached data available while offline:', { cacheKey });
      setError(new Error('No internet connection available'));
      onError?.(new Error('No internet connection available'));
      return;
    }
    
    // Check cache first when online
    if (!force && !skipCache) {
      let cacheResult;
      
      if (useHybridCache) {
        // Use hybrid cache for enhanced performance
        cacheResult = await getDataAsync<T>(cacheKey, { 
          ttl: maxAge, 
          useHybridCache: true 
        });
      } else {
        // Use legacy cache
        cacheResult = getData<T>(cacheKey, maxAge);
      }
      
      logger.debug('Checking cache:', { 
        status: cacheResult.status,
        source: cacheResult.source,
        cacheKey,
        age: cacheResult.age,
        maxAge: cacheResult.maxAge || maxAge,
        dataType: cacheResult.data ? typeof cacheResult.data : null,
        isArray: Array.isArray(cacheResult.data),
        length: Array.isArray(cacheResult.data) ? cacheResult.data.length : null,
        useHybridCache
      });
      
      if (cacheResult.data && cacheResult.status === 'valid') {
        const validatedData = validateData(cacheResult.data);
        if (validatedData) {
          logger.debug('Using cached data:', { 
            cacheKey, 
            dataLength: Array.isArray(validatedData) ? validatedData.length : null 
          });
          setData(validatedData);
          onSuccess?.(validatedData);
          return;
        }
      }

      // If cache expired or user changed, we should fetch
      // If not_found, we might want to avoid fetching if we recently tried
      if (cacheResult.status === 'not_found') {
        const lastAttempt = lastFetchAttemptRef.current[cacheKey];
        const now = Date.now();
        if (lastAttempt && now - lastAttempt < NOT_FOUND_RETRY_DELAY) {
          logger.debug('Skipping fetch for not_found data, too soon to retry', {
            cacheKey,
            lastAttempt,
            timeSinceLastAttempt: now - lastAttempt
          });
          return;
        }
      }
    }

    // Check for existing in-flight request
    const existingRequest = getInFlightRequest<T>(cacheKey);
    if (existingRequest) {
      logger.debug('Reusing existing in-flight request:', { cacheKey });
      setLoading(true);
      setError(null);
      
      try {
        const newData = await existingRequest;
        
        // Update last fetch attempt time
        lastFetchAttemptRef.current[cacheKey] = Date.now();
      
      logger.debug('Fetch result:', { 
        hasData: !!newData, 
        dataType: typeof newData,
        isArray: Array.isArray(newData),
        length: Array.isArray(newData) ? newData.length : null,
        firstItem: Array.isArray(newData) ? newData[0] : null
      });
      
      if (!mountedRef.current) {
        logger.debug('Component unmounted, abandoning update');
        return;
      }

      const validatedData = validateData(newData);
      logger.debug('Data validation result:', { 
        cacheKey,
        isValid: validatedData !== null || newData === null,
        validatedData,
        originalData: newData
      });
      
      if (!validatedData && validatedData !== null) {
        logger.error('Data validation failed:', { 
          cacheKey,
          dataType: typeof newData,
          isArray: Array.isArray(newData),
          sample: Array.isArray(newData) ? newData[0] : newData
        });
        throw new Error('Invalid data received from server');
      }

      logger.debug('Setting validated data:', { 
        cacheKey, 
        dataLength: Array.isArray(validatedData) ? validatedData.length : null 
      });
      setData(validatedData);
      
      // Reset retry count on successful fetch
      if (retryCountRef.current[cacheKey] > 0) {
        logger.debug('Resetting retry count after successful fetch:', { 
          cacheKey, 
          previousRetryCount: retryCountRef.current[cacheKey] 
        });
        retryCountRef.current[cacheKey] = 0;
      }
      
      if (!skipCache) {
        logger.debug('Updating cache:', { 
          cacheKey, 
          dataLength: Array.isArray(validatedData) ? validatedData.length : null,
          useHybridCache
        });
        
        if (useHybridCache) {
          // Use hybrid cache for enhanced performance
          await setCacheData(cacheKey, validatedData, { 
            ttl: maxAge, 
            useHybridCache: true 
          });
        } else {
          // Use legacy cache
          setCacheData(cacheKey, validatedData);
        }
      }
      
      onSuccess?.(validatedData);
        return; // Exit early for existing request case
      } catch (err) {
        if (!mountedRef.current) return;
        
        // Handle error from existing request
        logger.error('Error from reused request:', { cacheKey, error: err });
        setError(err instanceof Error ? err : new Error('Failed to fetch data'));
        onError?.(err instanceof Error ? err : new Error('Failed to fetch data'));
        return;
      } finally {
        if (mountedRef.current) {
          setLoading(false);
        }
      }
    }
    
    // No existing request, create a new one
    setLoading(true);
    setError(null);
    fetchingRef.current = true;
    
    // Create the fetch promise
    const fetchPromise = fetchFn();
    
    // Store it as an in-flight request
    setInFlightRequest(cacheKey, fetchPromise);
    
    try {
      logger.debug('Starting new fetch:', { cacheKey });
      const newData = await fetchPromise;
      
      // Update last fetch attempt time
      lastFetchAttemptRef.current[cacheKey] = Date.now();
      
      logger.debug('Fetch result:', { 
        hasData: !!newData, 
        dataType: typeof newData,
        isArray: Array.isArray(newData),
        length: Array.isArray(newData) ? newData.length : null,
        firstItem: Array.isArray(newData) ? newData[0] : null
      });
      
      if (!mountedRef.current) {
        logger.debug('Component unmounted, abandoning update');
        return;
      }

      const validatedData = validateData(newData);
      logger.debug('Data validation result:', { 
        cacheKey,
        isValid: validatedData !== null || newData === null,
        validatedData,
        originalData: newData
      });
      
      if (!validatedData && validatedData !== null) {
        logger.error('Data validation failed:', { 
          cacheKey,
          dataType: typeof newData,
          isArray: Array.isArray(newData),
          sample: Array.isArray(newData) ? newData[0] : newData
        });
        throw new Error('Invalid data received from server');
      }

      logger.debug('Setting validated data:', { 
        cacheKey, 
        dataLength: Array.isArray(validatedData) ? validatedData.length : null 
      });
      setData(validatedData);
      
      // Reset retry count on successful fetch
      if (retryCountRef.current[cacheKey] > 0) {
        logger.debug('Resetting retry count after successful fetch:', { 
          cacheKey, 
          previousRetryCount: retryCountRef.current[cacheKey] 
        });
        retryCountRef.current[cacheKey] = 0;
      }
      
      if (!skipCache) {
        logger.debug('Updating cache:', { 
          cacheKey, 
          dataLength: Array.isArray(validatedData) ? validatedData.length : null,
          useHybridCache
        });
        
        if (useHybridCache) {
          // Use hybrid cache for enhanced performance
          await setCacheData(cacheKey, validatedData, { 
            ttl: maxAge, 
            useHybridCache: true 
          });
        } else {
          // Use legacy cache
          setCacheData(cacheKey, validatedData);
        }
      }
      
      onSuccess?.(validatedData);
    } catch (err) {
      if (!mountedRef.current) return;

      // Check if it's a network error
      const isConnectionError = err instanceof Error && isNetworkError(err);
      if (isConnectionError) {
        // Mark that we've had a connection problem
        networkUtils.markConnectionProblem();
      }

      // Create a sanitized error message that doesn't contain sensitive URLs
      let errorMessage = 'Failed to fetch data';
      
      if (err instanceof Error) {
        // Check if it's a network error
        if (isConnectionError) {
          errorMessage = navigator.onLine 
            ? 'Connection to database failed' 
            : 'No internet connection available';
            
          // Use cached data if available for network errors
          const cacheResult = getData<T>(cacheKey, Infinity);
          if (cacheResult.data) {
            const validatedData = validateData(cacheResult.data);
            if (validatedData) {
              logger.debug('Using cached data after network error:', { cacheKey });
              setData(validatedData);
              // Still set an error so UI can show connection warning if needed
              setError(new Error(errorMessage));
              setLoading(false);
              return;
            }
          }
        } else if (!err.message.includes('URL') && !err.message.includes('supabase')) {
          // Only use the original error message if it doesn't contain sensitive info
          errorMessage = err.message;
        }
      }
      
      const error = new Error(errorMessage);
      
      // Log network errors less verbosely
      if (isConnectionError) {
        logger.debug('Network error:', { 
          cacheKey, 
          error: errorMessage,
          retryCount: retryCountRef.current[cacheKey] || 0
        });
      } else {
        logger.error('Fetch error:', { 
          cacheKey, 
          error: errorMessage,
          retryCount: retryCountRef.current[cacheKey] || 0
        });
      }
      
      setError(error);
      onError?.(error);
      
      // Implement exponential backoff for retries
      if (!retryCountRef.current[cacheKey]) {
        retryCountRef.current[cacheKey] = 0;
      }
      retryCountRef.current[cacheKey]++;
      
      const retryCount = retryCountRef.current[cacheKey];
      const maxRetries = 5;
      
      // Only retry for network errors or temporary failures
      const shouldRetry = (isConnectionError || err?.code === 'NETWORK_ERROR' || err?.code === 'ECONNRESET') 
        && retryCount <= maxRetries
        && enabled
        && mountedRef.current;
      
      if (shouldRetry) {
        const backoffDelay = getBackoffDelay(retryCount - 1);
        logger.debug('Scheduling retry with backoff:', { 
          cacheKey, 
          retryCount, 
          backoffDelay,
          nextRetryIn: `${(backoffDelay / 1000).toFixed(1)}s`
        });
        
        // Clear any existing timer
        if (backoffTimerRef.current) {
          clearTimeout(backoffTimerRef.current);
        }
        
        // Schedule retry with backoff
        backoffTimerRef.current = setTimeout(() => {
          if (mountedRef.current && enabled) {
            logger.debug('Retrying after backoff:', { cacheKey, retryCount });
            fetchData(true);
          }
        }, backoffDelay);
      } else if (retryCount > maxRetries) {
        logger.error('Max retries reached, giving up:', { cacheKey, retryCount });
        // Reset retry count after max retries
        retryCountRef.current[cacheKey] = 0;
      }
      
      // Only send to error handler if it's not a simple network error
      if (navigator.onLine && !isConnectionError) {
        await handleError(err, {
          context: 'fetch',
          cacheKey
        });
      }
    } finally {
      // Clean up in-flight request
      removeInFlightRequest(cacheKey);
      fetchingRef.current = false;
      if (mountedRef.current) {
        setLoading(false);
      }
    }
  }, [
    fetchFn,
    cacheKey,
    maxAge,
    skipCache,
    getData,
    setCacheData,
    onSuccess,
    onError,
    handleError,
    validateData,
    useHybridCache,
    getDataAsync,
    getBackoffDelay,
    enabled,
    getInFlightRequest,
    setInFlightRequest,
    removeInFlightRequest
  ]);

  const refresh = useCallback(() => fetchData(true), [fetchData]);

  // Also add a reconnect handler
  useEffect(() => {
    // When network reconnects, refresh the data
    const unsubscribe = networkUtils.onReconnect(() => {
      if (mountedRef.current) {
        logger.debug('Network reconnected, refreshing data:', { cacheKey });
        refresh();
      }
    });
    
    return unsubscribe;
  }, [refresh, cacheKey]);

  useEffect(() => {
    mountedRef.current = true;
    if (enabled) {
      fetchData();
    }

    return () => {
      mountedRef.current = false;
      // Clear any pending retry timers
      if (backoffTimerRef.current) {
        clearTimeout(backoffTimerRef.current);
        backoffTimerRef.current = null;
      }
    };
  }, [fetchData, enabled]);

  return {
    data,
    loading,
    error,
    refresh
  };
} 